import { useMutation, useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";

import { CUSTOMER_QUERY_KEYS } from "common/constants";
import { patch, post } from "common/helpers/HTTP";
import type { AgencyPlan, CustomerInfo, Promotion } from "common/types";

async function createSubscriptionRequest(
  count: number,
  paymentSourceId: string,
  planPricingId: string,
): Promise<AgencyPlan> {
  return (
    await post(`subscriptions`, {
      seat_count: count,
      payment_source_id: paymentSourceId,
      item_price_id: planPricingId,
    })
  ).promotion;
}
async function updateSubscriptionRequest(
  count: number,
  paymentSourceId: string,
  planPricingId: string,
): Promise<AgencyPlan> {
  return (
    await patch(`subscriptions`, {
      seat_count: count,
      payment_source_id: paymentSourceId,
      item_price_id: planPricingId,
    })
  ).promotion;
}

export default function useCustomerSubscriptionMutations() {
  const queryClient = useQueryClient();

  const {
    mutateAsync: createCustomerSubscription,
    isPending: isCreatingCustomerSubscription,
    error: createCustomerSubscriptionError,
  } = useMutation({
    mutationFn: ({
      count,
      paymentSourceId,
      planPricingId,
    }: {
      count: number;
      paymentSourceId: string;
      planPricingId: string;
    }) => createSubscriptionRequest(count, paymentSourceId, planPricingId),
    onSuccess: (promotion) => {
      queryClient.setQueryData<Promotion[]>(
        CUSTOMER_QUERY_KEYS.promotions,
        (prevPromotions) => {
          if (prevPromotions) {
            return [...prevPromotions, promotion];
          } else {
            return [promotion];
          }
        },
      );
      queryClient.setQueryData<CustomerInfo>(
        CUSTOMER_QUERY_KEYS.customerInfo,
        (prevCustomerInfo) => {
          return {
            ...prevCustomerInfo,
            agency_subscription_status: promotion.subscription.status,
          };
        },
      );
    },
  });

  const {
    mutateAsync: updateCustomerSubscription,
    isPending: isUpdatingCustomerSubscription,
    error: updateCustomerSubscriptionError,
  } = useMutation({
    mutationFn: ({
      count,
      paymentSourceId,
      planPricingId,
    }: {
      count: number;
      paymentSourceId: string;
      planPricingId: string;
    }) => updateSubscriptionRequest(count, paymentSourceId, planPricingId),
    onSuccess: (updatedPromotion) => {
      queryClient.setQueryData<Promotion[]>(
        CUSTOMER_QUERY_KEYS.promotions,
        (prevPromotions) =>
          produce(prevPromotions, (promotionsDraft) => {
            for (let i = 0; i < promotionsDraft.length; i += 1) {
              const promotion = promotionsDraft[i];
              if (
                promotion.type === "agency" &&
                promotion.subscription.id === updatedPromotion.subscription.id
              ) {
                promotionsDraft[i] = updatedPromotion;
                return promotionsDraft;
              }
            }
          }),
      );
      queryClient.setQueryData<CustomerInfo>(
        CUSTOMER_QUERY_KEYS.customerInfo,
        (prevCustomerInfo) => {
          return {
            ...prevCustomerInfo,
            agency_subscription_status: updatedPromotion.subscription.status,
          };
        },
      );
      queryClient.invalidateQueries({
        queryKey: CUSTOMER_QUERY_KEYS.subscriptionKeys.renewalEstimate(),
      });
    },
  });

  return {
    createCustomerSubscription,
    isCreatingCustomerSubscription,
    createCustomerSubscriptionError,

    updateCustomerSubscription,
    isUpdatingCustomerSubscription,
    updateCustomerSubscriptionError,
  };
}
